import { useRouter } from 'next/router'

import clsx from 'clsx'
import useSWR from 'swr'

import { ResourceFeedDocument, ResourceOrder } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { normalizeImage } from '@/lib/contentful/normalizers/image'

import { Card } from '@/components/Card'

import { serializeCacheKey } from '../../../../../lib/contentful/utils'

type Props = {
	className?: string
	type: 'Webinar' | 'Report' | 'Guide'
	count?: number
	CTALinkText?: string
}

export const RESOURCE_LIMIT = 12

const getPathType = (type: Props['type']) => {
	switch (type) {
		case 'Guide':
			return 'guides'
		case 'Report':
			return 'reports'
		default:
			return 'webinars'
	}
}

export function ResourcesFeed({
	className,
	type,
	CTALinkText = 'Watch Now',
	count = RESOURCE_LIMIT
}: Props) {
	const { locale, isPreview } = useRouter()
	const contentful = client({ preview: isPreview })

	const { data } = useSWR(serializeCacheKey('resourceFeed', { locale, type, limit: count }), () =>
		contentful.request(ResourceFeedDocument, {
			where: { status: true, type, cardImage_exists: true },
			order: [ResourceOrder.PublishedDateDesc],
			limit: count,
			locale,
			preview: isPreview
		})
	)

	if (!data) {
		return null
	}

	return (
		<div
			data-testid="resource-feed"
			className={clsx(
				className,
				'grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-[30px] gap-y-15'
			)}
		>
			{data?.resourceCollection?.items?.map((item) => {
				const title = item?.title
				const href = `/resources/${getPathType(type)}/${item?.slug}/`
				const image = normalizeImage(item?.cardImage)

				if (!title || !item.slug) {
					return null
				}

				return (
					<Card
						data-testid="resource-feed-card"
						key={item?.slug}
						title={title}
						imageSrc={image}
						imageAlt={image?.alt ?? title}
						linkText={CTALinkText}
						linkURL={{ href, target: '_self' }}
						variant="dark"
						imageRatio="none"
						isContained={false}
						verticalContentAlign="top"
					/>
				)
			})}
		</div>
	)
}
